import React, { useState, useEffect, useContext } from 'react';
import {
  Button, Typography, CircularProgress, Select, MenuItem, FormControl, InputLabel,
  TextField, Checkbox, ListItemText, Divider, Chip,
} from '@material-ui/core';
import {
  getIntegrationConfig, setIntegrationConfig, testIntegration,
  getIntegrationContactTypes, syncIntegrationNow,
} from '../../Actions';
import { AlertContext } from '../../Context/AlertContextProvider';

// Admin screen for one external-system integration, selected by `provider`.
// Nothing here is Albi-specific: the server returns the provider's name and
// capabilities, so a second system reuses this screen unchanged.
//
// The API key is write-only from the browser's point of view: the server sends a
// masked hint and never the value, so the field starts empty and an empty save
// means "leave it alone". Typing a new one replaces it.
const IntegrationSettings = ({ provider = 'albi' }) => {
  const { handleAlertOpen, setMessageType, setMessage } = useContext(AlertContext);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const [enabled, setEnabled] = useState(false);
  const [apiKey, setApiKey] = useState('');          // only ever a NEW key
  const [keyHint, setKeyHint] = useState(null);
  const [hasKey, setHasKey] = useState(false);
  const [baseUrl, setBaseUrl] = useState('');
  const [contactTypeIds, setContactTypeIds] = useState([]);
  const [status, setStatus] = useState({});

  const [types, setTypes] = useState([]);
  const [typesError, setTypesError] = useState(null);
  const [testResult, setTestResult] = useState(null);

  const alert = (type, text) => {
    setMessageType(type);
    setMessage(text);
    handleAlertOpen();
  };

  const load = () => {
    setLoading(true);
    getIntegrationConfig(provider)
      .then((res) => {
        const d = res.data?.data ?? {};
        setEnabled(!!d.enabled);
        setHasKey(!!d.has_api_key);
        setKeyHint(d.api_key_hint ?? null);
        setBaseUrl(d.base_url ?? '');
        setContactTypeIds((d.contact_type_ids ?? []).map(String));
        setStatus({
          mapped: d.mapped_contacts ?? 0,
          errored: d.errored_contacts ?? 0,
          lastSynced: d.last_synced_dttm ?? null,
        });
      })
      .catch(() => alert('error', 'Could not load the CRM settings'))
      .finally(() => setLoading(false));
  };

  useEffect(load, [provider]);

  // The picker can only be filled once a key is stored, so this is a separate
  // call that is allowed to fail without breaking the form.
  useEffect(() => {
    if (!hasKey) return;
    getIntegrationContactTypes(provider)
      .then((res) => setTypes(res.data?.data ?? []))
      .catch((err) => setTypesError(err?.response?.data?.message ?? 'Could not load contact types'));
  }, [hasKey, provider]);

  const save = () => {
    setSaving(true);
    setIntegrationConfig(provider, {
      enabled,
      api_key: apiKey || undefined,
      base_url: baseUrl,
      contact_type_ids: contactTypeIds,
    })
      .then(() => {
        alert('success', 'CRM settings saved');
        setApiKey('');
        load();
      })
      .catch((err) => alert('error', err?.response?.data?.message ?? 'Could not save the CRM settings'))
      .finally(() => setSaving(false));
  };

  const test = () => {
    setTesting(true);
    setTestResult(null);
    testIntegration(provider, { api_key: apiKey || undefined, base_url: baseUrl })
      .then((res) => setTestResult(res.data?.data ?? { ok: false, reason: 'No answer' }))
      .catch((err) => setTestResult({ ok: false, reason: err?.message ?? 'Request failed' }))
      .finally(() => setTesting(false));
  };

  const sync = () => {
    setSyncing(true);
    syncIntegrationNow(provider)
      .then((res) => {
        const s = res.data?.data ?? {};
        alert('success', `Sync complete — ${s.created ?? 0} created, ${s.updated ?? 0} updated, ${s.linked ?? 0} linked, ${s.unchanged ?? 0} unchanged`);
        load();
      })
      .catch((err) => alert('error', err?.response?.data?.reason ?? 'Sync failed'))
      .finally(() => setSyncing(false));
  };

  if (loading) return <CircularProgress size={24} />;

  const labelFor = (id) => {
    const t = types.find((x) => String(x.id) === String(id));
    // The catalogue genuinely contains blank names, so fall back to the id
    // rather than rendering an empty chip.
    return t ? (t.name || `(unnamed ${t.id})`) : id;
  };

  return (
    <div style={{ padding: '10px 0', minWidth: 520 }}>
      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 14 }}>
        Pulls contacts from the connected system into your leads, so staff can find a customer the
        project manager already entered. Runs hourly once enabled; use
        Sync Now to try it first.
      </Typography>

      <TextField
        fullWidth
        label={hasKey ? `API key (stored: ${keyHint}) — type a new one to replace it` : 'API key'}
        value={apiKey}
        onChange={(e) => setApiKey(e.target.value)}
        placeholder={hasKey ? 'Leave blank to keep the current key' : 'Paste the key from the connected system'}
        type="password"
        size="small"
        style={{ marginBottom: 14 }}
      />

      <TextField
        fullWidth
        label="API base URL"
        value={baseUrl}
        onChange={(e) => setBaseUrl(e.target.value)}
        placeholder="https://publicapi.albiware.com"
        size="small"
        style={{ marginBottom: 14 }}
      />

      <FormControl fullWidth style={{ marginBottom: 6 }}>
        <InputLabel shrink>Contact types to sync</InputLabel>
        <Select
          multiple
          displayEmpty
          value={contactTypeIds}
          onChange={(e) => setContactTypeIds(e.target.value.map(String))}
          renderValue={(selected) =>
            selected.length === 0
              ? 'Every contact'
              : selected.map(labelFor).join(', ')
          }
        >
          {types.map((t) => (
            <MenuItem key={t.id} value={String(t.id)}>
              <Checkbox checked={contactTypeIds.includes(String(t.id))} />
              <ListItemText primary={t.name || `(unnamed ${t.id})`} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: 14 }}>
        {typesError
          ? typesError
          : !hasKey
            ? 'Save an API key to load the list of contact types.'
            : contactTypeIds.length === 0
              ? 'Nothing selected means every contact is pulled — including adjusters, plumbers and insurance companies. Pick the customer-facing types.'
              : `${contactTypeIds.length} type${contactTypeIds.length === 1 ? '' : 's'} selected.`}
      </Typography>

      <FormControl style={{ minWidth: 220, marginBottom: 14 }}>
        <InputLabel shrink>Scheduled sync</InputLabel>
        <Select value={enabled ? 1 : 0} onChange={(e) => setEnabled(!!e.target.value)}>
          <MenuItem value={1}>On — sync hourly</MenuItem>
          <MenuItem value={0}>Off</MenuItem>
        </Select>
      </FormControl>

      <Divider style={{ marginBottom: 12 }} />

      <div style={{ marginBottom: 12 }}>
        <Typography variant="caption" color="textSecondary" style={{ display: 'block' }}>
          {status.mapped
            ? `${status.mapped} contact${status.mapped === 1 ? '' : 's'} linked to Albi${status.lastSynced ? `, last synced ${new Date(status.lastSynced).toLocaleString()}` : ''}.`
            : 'No contacts linked yet.'}
          {status.errored ? ` ${status.errored} had an error on the last attempt.` : ''}
        </Typography>
      </div>

      {testResult ? (
        <div style={{ marginBottom: 12 }}>
          <Chip
            size="small"
            label={testResult.ok ? 'Connection OK' : 'Connection failed'}
            style={{
              backgroundColor: testResult.ok ? '#2E7D32' : '#C62828',
              color: '#FFFFFF',
              marginRight: 8,
            }}
          />
          <Typography variant="caption" color="textSecondary">
            {testResult.ok
              ? `${testResult.contact_type_count} contact types visible${testResult.quota_remaining_hour != null ? ` · ${testResult.quota_remaining_hour} requests left this hour` : ''}`
              : `${testResult.reason}${testResult.track_id ? ` (track id ${testResult.track_id})` : ''}`}
          </Typography>
        </div>
      ) : undefined}

      <div>
        <Button
          size="small"
          variant="contained"
          onClick={save}
          disabled={saving}
          style={{ backgroundColor: '#4E564E', color: '#FFFFFF', marginRight: 10 }}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={test}
          disabled={testing || (!hasKey && !apiKey)}
          style={{ borderColor: '#4E564E', color: '#4E564E', marginRight: 10 }}
        >
          {testing ? 'Testing…' : 'Test Connection'}
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={sync}
          disabled={syncing || !hasKey}
          style={{ borderColor: '#4E564E', color: '#4E564E' }}
        >
          {syncing ? 'Syncing…' : 'Sync Now'}
        </Button>
      </div>
    </div>
  );
};

export default IntegrationSettings;
